import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, Plus, Download, Upload, Trash2, Eye, Filter,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { getCases, batchDeleteCases, softDeleteCase, createCase } from '../lib/api'
import { exportCasesToExcel } from '../lib/excel'
import { useAuth } from '../hooks/useAuth'
import type { Case } from '../types'

export default function CasesList() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [cases, setCases] = useState<Case[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadCases()
  }, [])

  const loadCases = async () => {
    try {
      setLoading(true)
      const data = await getCases()
      setCases(data || [])
      setError(null)
    } catch (err) {
      console.error('加载案件失败:', err)
      setError('加载案件失败，请刷新重试')
    } finally {
      setLoading(false)
    }
  }

  const filteredCases = cases.filter(c => {
    const matchSearch = !search ||
      c.case_number?.includes(search) ||
      c.title.includes(search) ||
      c.opposing_party?.includes(search)
    const matchStatus = statusFilter === 'all' || c.status === statusFilter
    return matchSearch && matchStatus
  })

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const toggleAll = () => {
    if (selectedIds.size === filteredCases.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredCases.map(c => c.id)))
    }
  }

  // 批量导入
  const handleBatchImport = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx,.xls,.csv'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        const data = await file.text()
        const workbook = XLSX.read(data, { type: 'binary' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[]

        if (jsonData.length === 0) {
          alert('文件中没有数据，请检查格式')
          return
        }

        // 解析并创建案件
        const results = { success: 0, failed: 0, errors: [] as string[] }

        for (const row of jsonData) {
          try {
            const caseData = {
              case_number: row['案号'] || null,
              title: row['案由'] || '',
              case_type: mapCaseType(row['案件类型']),
              party_role: mapPartyRole(row['我方角色']),
              opposing_party: row['对方当事人'] || '',
              court: row['法院'] || null,
              claim_amount: row['标的金额'] ? parseFloat(row['标的金额']) : null,
              status: mapStatus(row['案件状态']),
              notes: row['备注'] || null,
              filing_date: row['立案日期'] || new Date().toISOString().slice(0, 10),
              deleted_at: null,
            }

            await createCase(caseData)
            results.success++
          } catch (err: any) {
            results.failed++
            results.errors.push(`第${jsonData.indexOf(row) + 1}行: ${err?.message || '未知错误'}`)
          }
        }

        alert(`导入完成！成功: ${results.success} 条，失败: ${results.failed} 条`)
        if (results.errors.length > 0) {
          console.error('导入失败详情:', results.errors)
        }
        await loadCases()
      } catch (err) {
        alert('文件解析失败，请检查格式是否正确')
      }
    }
    input.click()
  }

  // 映射案件类型
  const mapCaseType = (type: string): 'civil_litigation' | 'trademark_admin' | 'other' => {
    const map: Record<string, 'civil_litigation' | 'trademark_admin' | 'other'> = {
      '民事诉讼': 'civil_litigation',
      '商标行政诉讼': 'trademark_admin',
      '其他': 'other',
    }
    return map[type] || 'other'
  }

  // 映射角色
  const mapPartyRole = (role: string): 'plaintiff' | 'defendant' | 'third_party' => {
    const map: Record<string, 'plaintiff' | 'defendant' | 'third_party'> = {
      '原告': 'plaintiff',
      '被告': 'defendant',
      '第三人': 'third_party',
    }
    return map[role] || 'plaintiff'
  }

  // 映射状态
  const mapStatus = (status: string): 'pending' | 'active' | 'closed' | 'suspended' => {
    const map: Record<string, 'pending' | 'active' | 'closed' | 'suspended'> = {
      '待发起': 'pending',
      '进行中': 'active',
      '已结案': 'closed',
      '中止': 'suspended',
    }
    return map[status] || 'pending'
  }

  // 批量导出
  const handleBatchExport = async () => {
    const casesToExport = selectedIds.size > 0
      ? cases.filter(c => selectedIds.has(c.id))
      : filteredCases
    
    try {
      const blob = await exportCasesToExcel(casesToExport)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `案件导出_${new Date().toISOString().slice(0,10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('导出失败，请重试')
    }
  }

  // 批量删除
  const handleBatchDelete = async () => {
    if (!confirm(`确定要删除选中的 ${selectedIds.size} 个案件吗？`)) return
    
    try {
      await batchDeleteCases(Array.from(selectedIds))
      alert(`已删除 ${selectedIds.size} 个案件`)
      setSelectedIds(new Set())
      await loadCases()
    } catch (err) {
      alert('删除失败，请重试')
    }
  }

  // 单个删除
  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除该案件吗？')) return
    
    try {
      await softDeleteCase(id)
      alert('已删除案件')
      await loadCases()
    } catch (err) {
      alert('删除失败，请重试')
    }
  }

  // 新建案件
  const handleNewCase = () => {
    navigate('/cases/new')
  }

  const roleLabel = (role: string) => {
    const map: Record<string, { label: string; color: string }> = {
      plaintiff: { label: '原告', color: 'bg-blue-100 text-blue-700' },
      defendant: { label: '被告', color: 'bg-yellow-100 text-yellow-700' },
      third_party: { label: '第三人', color: 'bg-purple-100 text-purple-700' },
    }
    const { label, color } = map[role] || { label: role, color: 'bg-gray-100 text-gray-700' }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
  }

  const statusLabel = (status: string) => {
    const map: Record<string, { label: string; color: string }> = {
      pending: { label: '待发起', color: 'bg-gray-100 text-gray-700' },
      active: { label: '进行中', color: 'bg-green-100 text-green-700' },
      closed: { label: '已结案', color: 'bg-gray-100 text-gray-600' },
      suspended: { label: '中止', color: 'bg-orange-100 text-orange-700' },
    }
    const { label, color } = map[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="搜索案号、案由、对方当事人..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">全部状态</option>
          <option value="active">进行中</option>
          <option value="closed">已结案</option>
          <option value="pending">待发起</option>
          <option value="suspended">中止</option>
        </select>
        <div className="flex-1" />
        {isAdmin && (
          <>
            <button
              onClick={handleBatchImport}
              className="btn btn-outline text-sm flex items-center gap-2"
            >
              <Upload size={14} /> 批量导入
            </button>
            <button
              onClick={handleBatchExport}
              className="btn btn-outline text-sm flex items-center gap-2"
            >
              <Download size={14} /> 批量导出
            </button>
            <button
              onClick={handleBatchDelete}
              className="btn text-sm flex items-center gap-2 text-red-600 border border-red-300 hover:bg-red-50"
            >
              <Trash2 size={14} /> 批量删除
            </button>
            <button
              onClick={handleNewCase}
              className="btn bg-indigo-600 text-white text-sm flex items-center gap-2 hover:bg-indigo-700"
            >
              <Plus size={14} /> 新建案件
            </button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Checkbox header */}
        <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selectedIds.size === filteredCases.length && filteredCases.length > 0}
              onChange={toggleAll}
              className="accent-indigo-600"
            />
            全选
          </label>
          <span>已选择 {selectedIds.size} / {filteredCases.length} 项</span>
          <div className="flex-1" />
          <span>每页 20 条 · 共 {filteredCases.length} 项</span>
        </div>

        {error && (
          <div className="p-4 text-center text-red-500">{error}</div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-10"></th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">案号</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">案由</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">我方角色</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">法院</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">状态</th>
              <th className="text-left py-3 px-4 font-medium text-gray-500">最近到期</th>
              <th className="text-right py-3 px-4 font-medium text-gray-500">标的金额</th>
              <th className="text-center py-3 px-4 font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredCases.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-gray-400">
                  {cases.length === 0 ? '暂无案件，点击"新建案件"添加' : '无匹配结果'}
                </td>
              </tr>
            ) : (
              filteredCases.map(c => (
                <tr
                  key={c.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/cases/${c.id}`)}
                >
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      className="accent-indigo-600"
                    />
                  </td>
                  <td className="py-3 px-4 font-medium text-indigo-600">{c.case_number}</td>
                  <td className="py-3 px-4">{c.title}</td>
                  <td className="py-3 px-4">{roleLabel(c.party_role)}</td>
                  <td className="py-3 px-4 text-gray-500 text-xs">{c.court}</td>
                  <td className="py-3 px-4">{statusLabel(c.status)}</td>
                  <td className="py-3 px-4">
                    {c.status === 'active' ? (
                      <span className="text-red-600 font-semibold text-xs">2天</span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    {c.claim_amount ? `¥${(c.claim_amount / 10000).toFixed(0)}万` : '—'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/cases/${c.id}`) }}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Eye size={14} className="text-gray-500" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(c.id) }}
                        className="p-1.5 hover:bg-red-100 rounded transition-colors ml-1"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
          <button className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40" disabled>‹</button>
          <button className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded">1</button>
          <button className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">2</button>
          <button className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">3</button>
          <button className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">›</button>
        </div>
      </div>
    </div>
  )
}
